import { Box, Grid, TextField, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import {
  BrandDealJourney,
  BrandDealJourneyDisplayName,
  DiscoverySource,
  DiscoverySourceDisplayName,
} from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";
import { routes } from "schemas/routes";

import BentoBrandsAutocomplete from "components/BentoBrandsAutocomplete";
import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import StyledCard from "components/Styled/Card";
import QuestionChip from "components/Styled/QuestionChip";
import StyledTextField from "components/Styled/TextField";
import { isPage } from "utils/general";
import { makeDeepCopy } from "utils/updateLocalState";

import StyledOnboardingPage from "../StyledOnboardingPage";
import styles from "../styles";
import AboutFormLinks from "./Links";
import {
  SocialMediaFields,
  SocialMediaLinks,
  isValidUsername,
  sanitizeInput,
} from "./helpers";

interface AboutFormProps {
  handleNext: (onboardingForm?: Map) => void;
  handleSkip?: () => void;
  handleBack?: () => void;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  isVAFlow?: boolean;
  hideDiscoveryQuestion?: boolean;
}

const AboutForm = ({
  handleNext,
  handleSkip,
  handleBack,
  onboardingForm,
  setOnboardingForm,
  hideDiscoveryQuestion = false,
}: AboutFormProps) => {
  const isProfilePage = isPage(routes.profile);
  const { profileDialogOpen } = useContext(OnboardingDialogsContext);
  const { setAlert } = useContext(AlertContext);
  const [linksValidation, setLinksValidation] = useState<{
    [key in string]: string | undefined;
  }>({});
  const [links, setLinks] = useState<SocialMediaLinks[]>([]);
  const [isOtherClicked, setIsOtherClicked] = useState(false);

  const handleChange = (e: CustomEvent) => {
    const { name, value } = e.target;
    const copy = makeDeepCopy(onboardingForm);
    copy[name] = value;
    setOnboardingForm(copy);
  };

  const handleSetDiscoverSource = (source?: string) => {
    if (!source) return;
    const copy = makeDeepCopy(onboardingForm);
    copy.discoverySource = source;
    setOnboardingForm(copy);
  };

  const clickOther = () => {
    setIsOtherClicked((prev) => !prev);
    setOnboardingForm((p) => {
      if (!p) return p;
      const copy = { ...p };
      copy.otherType = "";
      return copy;
    });
  };

  const handleSetBrandDealJourney = (journey?: BrandDealJourney) => {
    if (!journey) return;
    const copy = makeDeepCopy(onboardingForm);
    copy.brandDealJourney = journey;
    setOnboardingForm(copy);
  };

  const handleSubmit = () => {
    if (isProfilePage || profileDialogOpen) {
      if (Object.values(linksValidation).some((v) => v !== undefined)) {
        return;
      }

      for (const key in onboardingForm) {
        if (key in SocialMediaFields && onboardingForm[key]) {
          const input = sanitizeInput(onboardingForm[key]);
          if (input && !isValidUsername(input) && key !== "portfolioUrl") {
            return;
          }
        }
      }
    }

    if (profileDialogOpen && !onboardingForm?.location) {
      setAlert("Please fill in location", "error");
      return;
    }

    if (
      profileDialogOpen &&
      (!onboardingForm?.wishlistBrands ||
        onboardingForm?.wishlistBrands.length === 0)
    ) {
      setAlert("Please add at least one dream brand", "error");
      return;
    }
    handleNext(onboardingForm);
  };

  const handleLocationChange = (userLocation: UserLocation) => {
    setOnboardingForm((prev) => ({
      ...prev,
      city: userLocation.address,
      location: userLocation.country_code,
      addressComponents: userLocation.address_components,
      longitude: userLocation.longitude,
      latitude: userLocation.latitude,
    }));
  };

  const updateWishlistBrands = (newBrands: string[]) => {
    setOnboardingForm((prev) => {
      return {
        ...prev,
        wishlistBrands: newBrands,
      };
    });
  };

  return (
    <>
      <StyledOnboardingPage
        stepNumber={1}
        title={"Let's Get To Know You ✍️"}
        handleBack={handleBack}
        handleNext={handleSubmit}
        handleSkip={handleSkip}
      >
        <StyledCard styles={{ px: 3, py: 4 }}>
          {!profileDialogOpen && (
            <Box sx={{ mb: 2 }}>
              <StyledTextField
                value={onboardingForm?.name || ""}
                name="name"
                placeholder="First Last"
                onChange={handleChange}
                label="Your Name"
                required
                fullWidth
              />
            </Box>
          )}

          {!profileDialogOpen && (
            <Box sx={styles.questionBox} display="flex" flexDirection="column">
              <Box>
                <Typography gutterBottom>
                  How do you plan to use Bento? *
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" paragraph>
                  Select all that apply.
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <QuestionChip
                  onClick={() => {
                    setOnboardingForm((p) => {
                      setIsOtherClicked(false);
                      if (!p) return p;
                      const copy = { ...p };
                      if (copy.isUgcCreator) {
                        copy.isUgcCreator = false;
                      } else {
                        copy.isUgcCreator = true;
                      }
                      return copy;
                    });
                  }}
                  isSelected={!!onboardingForm?.isUgcCreator}
                  label="UGC"
                />
                <QuestionChip
                  onClick={() => {
                    setOnboardingForm((p) => {
                      setIsOtherClicked(false);
                      if (!p) return p;
                      const copy = { ...p };
                      if (copy.isInfluencer) {
                        copy.isInfluencer = false;
                      } else {
                        copy.isInfluencer = true;
                      }
                      return copy;
                    });
                  }}
                  isSelected={!!onboardingForm?.isInfluencer}
                  label="Influencer"
                />
                <Box>
                  <QuestionChip
                    isSelected={!!onboardingForm?.otherType || isOtherClicked}
                    label="Other (please specify):"
                    onClick={clickOther}
                  />
                  <TextField
                    value={onboardingForm?.otherType || ""}
                    onChange={(e: CustomEvent) => {
                      const { value } = e.target;
                      setOnboardingForm((p) => {
                        if (!p) return p;
                        const copy = { ...p };
                        copy.otherType = value;
                        return copy;
                      });
                    }}
                    variant="standard"
                    sx={{ ml: 1 }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {!profileDialogOpen && (
            <Box sx={styles.questionBox}>
              <Typography gutterBottom>
                Where are you in your brand deal journey?
              </Typography>
              <Typography variant="caption" color="textSecondary" paragraph>
                Select an option below.
              </Typography>

              <Grid container gap={1} sx={{ mb: 2 }} alignItems={"center"}>
                {Object.values(BrandDealJourney)?.map((journey) => (
                  <QuestionChip
                    isSelected={onboardingForm?.brandDealJourney === journey}
                    label={BrandDealJourneyDisplayName[journey]}
                    onClick={() => handleSetBrandDealJourney(journey)}
                    key={journey}
                  />
                ))}
              </Grid>
            </Box>
          )}

          {!isProfilePage && !hideDiscoveryQuestion && (
            <Box sx={styles.questionBox}>
              <Typography gutterBottom>How did you hear about us?</Typography>
              <Typography variant="caption" color="textSecondary" paragraph>
                Select an option below.
              </Typography>

              <Grid container gap={1} sx={{ mb: 2 }} alignItems={"center"}>
                {Object.values(DiscoverySource)?.map((source, index) => (
                  <QuestionChip
                    isSelected={onboardingForm?.discoverySource === source}
                    label={DiscoverySourceDisplayName[source]}
                    onClick={() => handleSetDiscoverSource(source)}
                    key={`discovery-source-${index}`}
                  />
                ))}
                {onboardingForm?.discoverySource === DiscoverySource.other && (
                  <TextField
                    variant="standard"
                    name="discoverySourceOther"
                    placeholder="Please specify"
                    onChange={handleChange}
                    value={onboardingForm?.discoverySourceOther || ""}
                  />
                )}
              </Grid>
            </Box>
          )}

          {(isProfilePage || profileDialogOpen) && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>
                  Location (city or zip code) *
                </Typography>
                <LocationAutocomplete
                  handleLocationChange={handleLocationChange}
                  userLocation={{
                    address: onboardingForm.city,
                    longitude: onboardingForm.longitude,
                    latitude: onboardingForm.latitude,
                    country_code: onboardingForm.location,
                    address_components: onboardingForm.addressComponents,
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>
                  Enter a few dream brands you'd love to work with *
                </Typography>
                <BentoBrandsAutocomplete
                  selectedBentoBrands={onboardingForm?.wishlistBrands || []}
                  updateSelectedBentoBrands={updateWishlistBrands}
                />
              </Box>
            </>
          )}

          {isProfilePage && (
            <AboutFormLinks
              onboardingForm={onboardingForm}
              setOnboardingForm={setOnboardingForm}
              setLinksValidation={setLinksValidation}
              links={links}
              setLinks={setLinks}
              validation={linksValidation}
            />
          )}
        </StyledCard>
      </StyledOnboardingPage>
    </>
  );
};

export default AboutForm;
